import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import { useGetAttorneyServicesPurchaseEligibilityQuery } from "../api/users";
import { skipToken } from "@reduxjs/toolkit/query";

const useCurrentUserPaymentData = () => {
  const {
    document,
    user,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserDocument();
  const {
    data: attorneyServicesPurchaseEligibility,
    isLoading: isAttorneyServicesPurchaseEligibilityLoading,
    error: attorneyServicesPurchaseEligibilityError,
  } = useGetAttorneyServicesPurchaseEligibilityQuery(
    user?.id && document?.stateId && !isUserLoading && !userError
      ? { userId: user.id }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const currentUserWillHireAttorney =
    document?.userAId === user?.id
      ? document?.userAWillHireAttorney
      : document?.userBWillHireAttorney;

  const purchaseItems = document?.purchaseItems;

  //PRENUP PRODUCT
  const isPrenupPaid = purchaseItems?.some(
    (item) =>
      item.paymentStatus === "SUCCESSFUL" &&
      item.product.productType === "PRENUP" &&
      item.deletedAt === null,
  );

  //NOTARIZATION PRODUCT
  const isOnlineNotarizationAvailableInUserState =
    document?.state.allowsOnlineNotary;
  const hasNotarizationProductForDocument = purchaseItems?.some(
    (item) =>
      item.paymentStatus === "SUCCESSFUL" &&
      (item.product.productType === "NOTARIZATION" ||
        item.product.productType === "REPRESENTATION") &&
      item.deletedAt === null,
  );
  const isNotarizationPaid = purchaseItems?.some(
    (item) =>
      item.paymentStatus === "SUCCESSFUL" &&
      item.product.productType === "NOTARIZATION" &&
      item.deletedAt === null,
  );

  //REPRESENTATION PRODUCT
  const representationPayment = purchaseItems?.find(
    (item) =>
      item.paymentStatus === "SUCCESSFUL" &&
      item.product.productType === "REPRESENTATION" &&
      item.userId === user?.id &&
      item.deletedAt === null,
  );

  const isRepresentationWaiting =
    representationPayment?.bookingStatus === "WAITING";
  const isRepresentationScheduled =
    representationPayment?.bookingStatus === "SCHEDULED";
  const isRepresentationBooked =
    representationPayment?.bookingStatus === "BOOKED";

  //QA PRODUCT
  const qAPayment = purchaseItems?.find(
    (item) =>
      item.paymentStatus === "SUCCESSFUL" &&
      item.product.productType === "Q_A" &&
      item.userId === user?.id &&
      (item.bookingStatus === "WAITING" ||
        item.bookingStatus === "SCHEDULED") &&
      item.deletedAt === null,
  );
  const isQAWaiting = qAPayment?.bookingStatus === "WAITING";
  const isQAScheduled = qAPayment?.bookingStatus === "SCHEDULED";
  const isQAProductAvailable =
    !isQAScheduled &&
    !isQAWaiting &&
    ((attorneyServicesPurchaseEligibility?.availableAttorneysForUser.length ??
      0) >= 1 ||
      (attorneyServicesPurchaseEligibility?.userAssignedAttorneyAndLawFirmIds
        .length ?? 0) >= 1);

  return {
    user,
    document,
    currentUserWillHireAttorney,
    //PRENUP PRODUCT
    isPrenupPaid,
    //NOTARIZATION PRODUCT
    isOnlineNotarizationAvailableInUserState,
    hasNotarizationProductForDocument,
    isNotarizationPaid,
    //REPRESENTATION PRODUCT
    representationPayment,
    maxRepresentationProductsCanPurchase:
      attorneyServicesPurchaseEligibility?.maxRepresentationProductsUserCanPurchase ??
      0,
    userCanPurchaseRepresentation:
      attorneyServicesPurchaseEligibility?.userCanPurchase ?? false,
    fianceCanPurchaseRepresentation:
      attorneyServicesPurchaseEligibility?.fianceCanPurchase ?? false,
    isRepresentationWaiting,
    isRepresentationScheduled,
    isRepresentationBooked,
    //QA PRODUCT
    isQAProductAvailable,
    qAPayment,
    isQAWaiting,
    isQAScheduled,
    isLoading: isUserLoading || isAttorneyServicesPurchaseEligibilityLoading,
    error: userError ?? attorneyServicesPurchaseEligibilityError,
    userDocumentError: userError,
    userDocumentLoading: isUserLoading,
  };
};

export default useCurrentUserPaymentData;
